import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  TestTube,
  TrendingUp,
  Tag,
  DollarSign,
} from "lucide-react";

type RuleType = "keyword" | "regex" | "merchant" | "amount_range" | "composite";

interface RuleFormData {
  name: string;
  description: string;
  ruleType: RuleType;
  pattern: string;
  category: string;
  accountCode: string;
  vatRate: string;
  priority: string;
  isActive: boolean;
}

const defaultFormData: RuleFormData = {
  name: "",
  description: "",
  ruleType: "keyword",
  pattern: "",
  category: "",
  accountCode: "",
  vatRate: "",
  priority: "50",
  isActive: true,
};

export default function CategorizationRules() {
  return (
    <DashboardLayout>
      <CategorizationRulesContent />
    </DashboardLayout>
  );
}

function CategorizationRulesContent() {
  const { selectedCompanyId } = useCompany();
  const utils = trpc.useUtils();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [testRuleId, setTestRuleId] = useState<number | null>(null);
  const [formData, setFormData] = useState<RuleFormData>(defaultFormData);
  const [testDescription, setTestDescription] = useState("");
  const [testAmount, setTestAmount] = useState("");
  const [testResult, setTestResult] = useState<any>(null);

  // Fetch company data
  const { data: companies } = trpc.company.list.useQuery();
  const selectedCompany = companies?.find(c => c.id === selectedCompanyId);

  // Fetch rules
  const { data: rules = [], isLoading } = trpc.categorization.list.useQuery(
    {
      companyId: selectedCompanyId,
    },
    {
      enabled: !!selectedCompanyId,
    }
  );

  // Create rule mutation
  const createRule = trpc.categorization.create.useMutation({
    onSuccess: () => {
      toast.success("Categorization rule has been created successfully.");
      utils.categorization.list.invalidate();
      setIsCreateDialogOpen(false);
      setFormData(defaultFormData);
    },
    onError: error => {
      toast.error(`Failed to create rule: ${error.message}`);
    },
  });

  // Update rule mutation
  const updateRule = trpc.categorization.update.useMutation({
    onSuccess: () => {
      toast.success("Categorization rule has been updated successfully.");
      utils.categorization.list.invalidate();
      setIsEditDialogOpen(false);
      setEditingRuleId(null);
      setFormData(defaultFormData);
    },
    onError: error => {
      toast.error(`Failed to update rule: ${error.message}`);
    },
  });

  // Delete rule mutation
  const deleteRule = trpc.categorization.delete.useMutation({
    onSuccess: () => {
      toast.success("Categorization rule has been deleted successfully.");
      utils.categorization.list.invalidate();
    },
    onError: error => {
      toast.error(`Failed to delete rule: ${error.message}`);
    },
  });

  // Test rule mutation
  const testRule = trpc.categorization.test.useMutation({
    onSuccess: data => {
      setTestResult(data);
      if (data.matches) {
        toast.success(
          `Match found! Category: ${data.result.category} (${data.result.confidence}% confidence)`
        );
      } else {
        toast.info("This rule does not match the test transaction.");
      }
    },
    onError: error => {
      toast.error(`Test failed: ${error.message}`);
    },
  });

  const handleCreateRule = () => {
    if (!selectedCompanyId) return;

    createRule.mutate({
      companyId: selectedCompanyId,
      name: formData.name,
      description: formData.description || undefined,
      ruleType: formData.ruleType,
      pattern: formData.pattern || undefined,
      category: formData.category,
      accountCode: formData.accountCode || undefined,
      vatRate: formData.vatRate ? parseInt(formData.vatRate) : undefined,
      priority: parseInt(formData.priority),
      isActive: formData.isActive ? 1 : 0,
    });
  };

  const handleUpdateRule = () => {
    if (!editingRuleId || !selectedCompanyId) return;

    updateRule.mutate({
      ruleId: editingRuleId,
      companyId: selectedCompanyId,
      name: formData.name,
      description: formData.description || undefined,
      pattern: formData.pattern || undefined,
      category: formData.category,
      accountCode: formData.accountCode || undefined,
      vatRate: formData.vatRate ? parseInt(formData.vatRate) : undefined,
      priority: parseInt(formData.priority),
      isActive: formData.isActive ? 1 : 0,
    });
  };

  const handleEditClick = (rule: any) => {
    setEditingRuleId(rule.id);
    setFormData({
      name: rule.name,
      description: rule.description || "",
      ruleType: rule.ruleType,
      pattern:
        rule.regexPattern ||
        rule.keywords?.join(", ") ||
        rule.merchantNames?.join(", ") ||
        "",
      category: rule.category,
      accountCode: rule.accountCode || "",
      vatRate: rule.vatRate ? String(rule.vatRate) : "",
      priority: String(rule.priority),
      isActive: rule.isActive === 1,
    });
    setIsEditDialogOpen(true);
  };

  const handleTestRule = () => {
    if (!testRuleId || !testDescription || !testAmount) {
      toast.error("Please provide transaction description and amount.");
      return;
    }

    testRule.mutate({
      ruleId: testRuleId,
      testTransaction: {
        description: testDescription,
        amount: parseFloat(testAmount) * 100, // Convert to cents
      },
    });
  };

  const getRuleTypeColor = (type: RuleType) => {
    switch (type) {
      case "keyword":
        return "bg-info";
      case "regex":
        return "bg-info";
      case "merchant":
        return "bg-success";
      case "amount_range":
        return "bg-warning";
      case "composite":
        return "bg-primary";
      default:
        return "bg-muted";
    }
  };

  if (!selectedCompany) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>No Company Selected</CardTitle>
            <CardDescription>
              Please select a company from the dashboard to manage
              categorization rules.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Categorization Rules</h1>
          <p className="text-muted-foreground mt-2">
            Manage automatic transaction categorization rules for{" "}
            {selectedCompany.name}
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setFormData(defaultFormData)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Categorization Rule</DialogTitle>
              <DialogDescription>
                Define a new rule to automatically categorize transactions
              </DialogDescription>
            </DialogHeader>
            <RuleForm
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleCreateRule}
              isSubmitting={createRule.isPending}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Rules</CardTitle>
          <CardDescription>
            Rules are applied in priority order (higher priority first)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading rules...
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No categorization rules yet. Create your first rule to get
              started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Matches</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule: any) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{rule.name}</div>
                        {rule.description && (
                          <div className="text-sm text-muted-foreground">
                            {rule.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRuleTypeColor(rule.ruleType)}>
                        {rule.ruleType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{rule.category}</div>
                        {rule.accountCode && (
                          <div className="text-sm text-muted-foreground">
                            {rule.accountCode}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{rule.priority}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        <span>{rule.matchCount || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={rule.isActive === 1 ? "default" : "secondary"}
                      >
                        {rule.isActive === 1 ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTestRuleId(rule.id);
                            setTestDescription("");
                            setTestAmount("");
                            setTestResult(null);
                            setIsTestDialogOpen(true);
                          }}
                        >
                          <TestTube className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(rule)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (!selectedCompanyId) return;
                            if (
                              confirm(
                                "Are you sure you want to delete this rule?"
                              )
                            ) {
                              deleteRule.mutate({
                                ruleId: rule.id,
                                companyId: selectedCompanyId,
                              });
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Categorization Rule</DialogTitle>
            <DialogDescription>Update the rule configuration</DialogDescription>
          </DialogHeader>
          <RuleForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleUpdateRule}
            isSubmitting={updateRule.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Test Categorization Rule</DialogTitle>
            <DialogDescription>
              Test this rule against sample transaction data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="test-description">Transaction Description</Label>
              <Input
                id="test-description"
                placeholder="e.g., COOP ZURICH PAYMENT"
                value={testDescription}
                onChange={e => setTestDescription(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="test-amount">Amount (CHF)</Label>
              <Input
                id="test-amount"
                type="number"
                step="0.01"
                placeholder="e.g., 125.50"
                value={testAmount}
                onChange={e => setTestAmount(e.target.value)}
              />
            </div>
            {testResult && (
              <Card
                className={
                  testResult.matches
                    ? "border-[var(--success)]"
                    : "border-[var(--warning)]"
                }
              >
                <CardHeader>
                  <CardTitle className="text-lg">
                    {testResult.matches ? "✓ Match Found" : "✗ No Match"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Category:</span>
                      <span className="font-medium">
                        {testResult.result.category}
                      </span>
                    </div>
                    {testResult.result.accountCode && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Account Code:
                        </span>
                        <span className="font-medium">
                          {testResult.result.accountCode}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Confidence:</span>
                      <span className="font-medium">
                        {testResult.result.confidence}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsTestDialogOpen(false)}
              >
                Close
              </Button>
              <Button onClick={handleTestRule} disabled={testRule.isPending}>
                {testRule.isPending ? "Testing..." : "Test Rule"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Rule Form Component
function RuleForm({
  formData,
  setFormData,
  onSubmit,
  isSubmitting,
}: {
  formData: RuleFormData;
  setFormData: (data: RuleFormData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="name">Rule Name *</Label>
        <Input
          id="name"
          placeholder="e.g., Coop Supermarket Purchases"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
        />
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Optional description of what this rule does"
          value={formData.description}
          onChange={e =>
            setFormData({ ...formData, description: e.target.value })
          }
        />
      </div>

      <div>
        <Label htmlFor="ruleType">Rule Type *</Label>
        <Select
          value={formData.ruleType}
          onValueChange={(value: RuleType) =>
            setFormData({ ...formData, ruleType: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="keyword">
              Keyword - Match specific words
            </SelectItem>
            <SelectItem value="regex">Regex - Pattern matching</SelectItem>
            <SelectItem value="merchant">
              Merchant - Swiss merchant names
            </SelectItem>
            <SelectItem value="amount_range">
              Amount Range - Based on amount
            </SelectItem>
            <SelectItem value="composite">
              Composite - Multiple conditions
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="pattern">Pattern *</Label>
        <Input
          id="pattern"
          placeholder={
            formData.ruleType === "keyword"
              ? "e.g., COOP, MIGROS"
              : formData.ruleType === "regex"
                ? "e.g., ^SBB.*TICKET$"
                : formData.ruleType === "merchant"
                  ? "e.g., COOP, MIGROS"
                  : "Leave empty for amount_range"
          }
          value={formData.pattern}
          onChange={e => setFormData({ ...formData, pattern: e.target.value })}
        />
        <p className="text-sm text-muted-foreground mt-1">
          {formData.ruleType === "keyword" &&
            "Comma-separated keywords to match"}
          {formData.ruleType === "regex" && "Regular expression pattern"}
          {formData.ruleType === "merchant" && "Comma-separated merchant names"}
          {formData.ruleType === "amount_range" &&
            "Pattern not used for amount ranges"}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Category *</Label>
          <Input
            id="category"
            placeholder="e.g., Office Supplies"
            value={formData.category}
            onChange={e =>
              setFormData({ ...formData, category: e.target.value })
            }
          />
        </div>
        <div>
          <Label htmlFor="accountCode">Account Code</Label>
          <Input
            id="accountCode"
            placeholder="e.g., 6300"
            value={formData.accountCode}
            onChange={e =>
              setFormData({ ...formData, accountCode: e.target.value })
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="vatRate">VAT Rate (basis points)</Label>
          <Input
            id="vatRate"
            type="number"
            placeholder="e.g., 810 for 8.1%"
            value={formData.vatRate}
            onChange={e =>
              setFormData({ ...formData, vatRate: e.target.value })
            }
          />
          <p className="text-sm text-muted-foreground mt-1">
            810 = 8.1%, 260 = 2.6%, 380 = 3.8%, 0 = exempt
          </p>
        </div>
        <div>
          <Label htmlFor="priority">Priority (0-100)</Label>
          <Input
            id="priority"
            type="number"
            min="0"
            max="100"
            value={formData.priority}
            onChange={e =>
              setFormData({ ...formData, priority: e.target.value })
            }
          />
          <p className="text-sm text-muted-foreground mt-1">
            Higher priority rules are checked first
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="isActive"
          checked={formData.isActive}
          onCheckedChange={checked =>
            setFormData({ ...formData, isActive: checked })
          }
        />
        <Label htmlFor="isActive">Active</Label>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save Rule"}
        </Button>
      </div>
    </div>
  );
}
